/**
 * Заведение поставщика с визитки.
 *
 * Обычный путь — сфотографировать карточку. Форма остаётся, но как запасной
 * выход, а не как главный: на выставке набирать китайское название компании
 * на телефоне никто не станет.
 *
 * Визитка часто двуязычная. Мы храним обе записи — оригинал и латиницу, —
 * потому что латиница нужна, чтобы искать и произносить, а оригинал нужен,
 * чтобы показать тому же поставщику и не потерять то, что напечатано.
 */
import { useEffect, useRef, useState } from 'react';
import { db, enqueue, type LocalExhibition, type LocalSupplier } from '../db';
import { apiFetch, syncNow } from '../sync';
import { grabFrame, openCamera } from '../lib/media';
import { ulid, supplierCode, placeTitle } from '@core';
import { whereIs } from '../lib/where';
import { findTwin, type Twin } from '../lib/twins';
import { guessCompanyName } from '../lib/guessname';

type Phase = 'camera' | 'reading' | 'review' | 'manual';

interface Bi { original?: string; latin?: string }
interface Card {
  companyName?: Bi; person?: Bi; position?: Bi; city?: Bi; address?: Bi;
  country?: string; phones?: string[]; emails?: string[]; websites?: string[];
  wechat?: string; whatsapp?: string; line?: string; telegram?: string;
  booth?: string; hall?: string;
  /** Гонконгский адрес одной строкой разбор раскладывает: здание, этаж, офис. */
  building?: Bi; floor?: string; unit?: string;
  /** Чем торгует, вторые названия, другие офисы — сервер собирает в одну заметку. */
  notes?: string;
  uncertain?: string[];
}

interface Props {
  exhibition: LocalExhibition;
  onDone: (supplierId: string) => void;
  onCancel: () => void;
  /** Здание узнало с визитки своё название или адрес — родитель обновит состояние. */
  onPlaceUpdated?: (ex: LocalExhibition) => void;
}

/**
 * Все контакты с визитки, без потерь.
 *
 * На карточке часто два телефона — свой и напарника, — и почт бывает две.
 * Форма показывает по одному полю, и раньше сохранялось ровно то, что в них
 * видно: второй телефон исчезал молча. Сайт не сохранялся вообще — он
 * читался, показывался человеку и пропадал при записи.
 *
 * Поэтому собираем объединение: то, что человек видит и мог поправить, плюс
 * всё остальное, что распозналось. Повторы отсекаем по цифрам и по тексту.
 */
function allContacts(
  form: { phone: string; email: string; wechat: string },
  card: Card,
): Array<{ id: string; kind: string; value: string }> {
  const out: Array<{ id: string; kind: string; value: string }> = [];
  const seen = new Set<string>();
  const key = (kind: string, v: string) => {
    const digits = v.replace(/\D/g, '');
    return kind === 'phone' && digits.length >= 7
      ? `phone:${digits.slice(-9)}`
      : `${kind}:${v.trim().toLowerCase()}`;
  };
  const add = (kind: string, value?: string) => {
    const v = (value ?? '').trim();
    if (!v) return;
    const k = key(kind, v);
    if (seen.has(k)) return;
    seen.add(k);
    out.push({ id: ulid(), kind, value: v });
  };

  // Сначала то, что человек видел в форме: он мог это поправить.
  add('phone', form.phone);
  add('email', form.email);
  add('wechat', form.wechat);
  for (const p of card.phones ?? []) add('phone', p);
  for (const e of card.emails ?? []) add('email', e);
  add('whatsapp', card.whatsapp);
  add('telegram', card.telegram);
  add('line', card.line);
  return out;
}

/** Латиница есть — берём её; нет — показываем оригинал, чтобы поле не пустовало. */
function pick(b?: Bi): string {
  return (b?.latin || b?.original || '').trim();
}

export default function CardScan({ exhibition, onDone, onCancel, onPlaceUpdated }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  /** Человек уже пробовал уйти дальше — значит, можно подсвечивать красным. */
  const [nudged, setNudged] = useState(false);

  const [phase, setPhase] = useState<Phase>('camera');
  const [error, setError] = useState('');
  const [shot, setShot] = useState<{ full: Blob; preview: Blob } | null>(null);
  const [shotUrl, setShotUrl] = useState('');
  const [card, setCard] = useState<Card>({});
  // Стенды этой выставки — чтобы узнать знакомую визитку и не завести двойника.
  const [known, setKnown] = useState<LocalSupplier[]>([]);
  const [twinRejected, setTwinRejected] = useState(false);
  const [merging, setMerging] = useState(false);

  const [form, setForm] = useState({
    companyName: '', companyNameLocal: '',
    person: '', personLocal: '', position: '', positionLocal: '',
    city: '', cityLocal: '', country: '', address: '', addressLocal: '',
    booth: '', hall: '', floor: '', office: '', phone: '', wechat: '', email: '', website: '',
  });
  const building = exhibition.kind === 'building';
  const [placeSaved, setPlaceSaved] = useState(false);
  const set = (k: keyof typeof form) => (e: any) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (phase !== 'camera') return;
    let dead = false;
    (async () => {
      try {
        const stream = await openCamera();
        if (dead) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (e: any) {
        setError(e?.name === 'NotAllowedError'
          ? 'Камера запрещена. Разрешите доступ в настройках сайта или заполните вручную.'
          : `Камера недоступна: ${e?.message ?? e}`);
      }
    })();
    return () => {
      dead = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [phase]);

  useEffect(() => () => { if (shotUrl) URL.revokeObjectURL(shotUrl); }, [shotUrl]);

  useEffect(() => { void db.suppliers.toArray().then(setKnown); }, []);

  async function shoot() {
    if (!videoRef.current) return;
    try {
      const frames = await grabFrame(videoRef.current);
      setShot(frames);
      setShotUrl(URL.createObjectURL(frames.preview));
      streamRef.current?.getTracks().forEach((t) => t.stop());
      setPhase('reading');
      await read(frames.preview);
    } catch (e: any) {
      setError(String(e?.message ?? e));
    }
  }

  async function read(image: Blob) {
    if (!navigator.onLine) {
      // Без сети визитка всё равно сохраняется. Разбор произойдёт при синке —
      // а до тех пор поставщик живёт с именем, которое вы впишете сами.
      setPhase('review');
      return;
    }
    try {
      const fd = new FormData();
      fd.append('file', image, 'card.webp');
      const res = await apiFetch('/api/scan-card', { method: 'POST', body: fd });
      if (!res.ok) {
        // Сервер объясняет причину словами — показываем её, а не код.
        const d = await res.json().catch(() => ({} as any));
        throw new Error(d?.error ?? `сервер ответил ${res.status}`);
      }
      const { card: c } = (await res.json()) as { card: Card };
      setCard(c ?? {});
      /*
       * Распознанное не затирает вписанное руками.
       *
       * Визитку переснимают, когда первый кадр вышел плохо, — и человек к
       * этому моменту уже мог что-то вписать сам. Раньше второй разбор
       * перезаписывал все поля подряд, включая пустыми: набранное исчезало
       * без предупреждения.
       */
      setForm((f) => {
        const take = (fresh: string | undefined, mine: string) => (mine.trim() ? mine : fresh ?? '');
        return {
          ...f,
          companyName: take(pick(c?.companyName), f.companyName),
          companyNameLocal: take(c?.companyName?.original, f.companyNameLocal),
          person: take(pick(c?.person), f.person),
          personLocal: take(c?.person?.original, f.personLocal),
          position: take(pick(c?.position), f.position),
          positionLocal: take(c?.position?.original, f.positionLocal),
          city: take(pick(c?.city), f.city),
          cityLocal: take(c?.city?.original, f.cityLocal),
          address: take(pick(c?.address), f.address),
          addressLocal: take(c?.address?.original, f.addressLocal),
          country: take(c?.country, f.country),
          booth: take(c?.booth, f.booth),
          hall: take(c?.hall, f.hall),
          floor: take(c?.floor, f.floor),
          office: take(c?.unit, f.office),
          phone: take(c?.phones?.[0], f.phone),
          email: take(c?.emails?.[0], f.email),
          website: take(c?.websites?.[0], f.website),
          wechat: take(c?.wechat, f.wechat),
        };
      });
      setPhase('review');
    } catch (e: any) {
      setError(`${e?.message ?? e} Впишите название сами — фото визитки сохранится, камни будут сниматься как обычно.`);
      setPhase('review');
    }
  }

  /**
   * Добавить к тому стенду, который уже есть.
   *
   * Это и есть второй подход к поставщику: новая визитка, новые контакты,
   * иногда сменившийся стенд — всё ложится в существующую карточку, и камни
   * продолжают нумероваться подряд. Заводить близнеца незачем.
   *
   * Пустых значений не шлём: сервер понимает пустую строку как «стереть»,
   * и второй, менее удачный снимок визитки затёр бы то, что распозналось
   * в первый раз.
   */
  async function addToExisting(twin: Twin) {
    const s = twin.supplier;
    setMerging(true);

    const have = new Set((s.contacts ?? []).map((c) => c.value.replace(/\s/g, '')));
    const fresh = allContacts(form, card)
      .filter((c) => !have.has(c.value.replace(/\s/g, '')));

    const patch: Record<string, string> = {};
    const keep = (field: string, value: string, current?: string) => {
      const v = value.trim();
      if (v && v !== (current ?? '')) patch[field] = v;
    };
    keep('booth', form.booth, s.booth);
    keep('hall', form.hall, s.hall);
    keep('floor', form.floor, s.floor);
    keep('office', form.office, s.office);
    keep('contactPerson', form.person, s.contactPerson);
    keep('position', form.position, s.position);
    keep('city', form.city, s.city);
    keep('country', form.country, s.country);
    keep('address', form.address, s.address);
    keep('website', form.website, s.website);
    if (card.notes && !s.notes) patch.notes = card.notes;

    const merged: LocalSupplier = {
      ...s,
      booth: patch.booth ?? s.booth,
      hall: patch.hall ?? s.hall,
      floor: patch.floor ?? s.floor,
      office: patch.office ?? s.office,
      contactPerson: patch.contactPerson ?? s.contactPerson,
      position: patch.position ?? s.position,
      city: patch.city ?? s.city,
      country: patch.country ?? s.country,
      address: patch.address ?? s.address,
      website: patch.website ?? s.website,
      notes: patch.notes ?? s.notes,
      contacts: [...(s.contacts ?? []), ...fresh],
    };
    await db.suppliers.put(merged);
    await enqueue('supplier.upsert', {
      id: s.id, companyName: s.companyName, ...patch, contacts: fresh,
    }, ulid());

    // Вторая визитка тоже сохраняется: на ней бывает то, чего не было на первой.
    if (shot) {
      await db.media.put({
        id: ulid(), subjectId: s.id, role: 'card', blob: shot.full,
        mime: shot.full.type, bytes: shot.full.size, uploaded: false,
        createdAt: new Date().toISOString(),
      });
    }

    void syncNow();
    setMerging(false);
    onDone(s.id);
  }

  async function save() {
    const name = form.companyName.trim() || form.companyNameLocal.trim();
    if (!name) return;
    try {
      await saveInner(name);
    } catch (e: any) {
      /*
       * Молчаливый отказ здесь — худшее из возможного.
       *
       * Заказчик вписывал названия руками и говорил, что ничего не
       * сохранилось. В этой функции не было ни одной ловушки ошибок: любой
       * сбой записи на устройстве (кончилось место, браузер закрыл базу)
       * просто ничего не делал, и экран оставался прежним. Теперь причина
       * видна, а набранное остаётся в форме.
       */
      setError(`Не удалось сохранить: ${e?.message ?? e}. Набранное на месте — попробуйте ещё раз.`);
      nameRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }

  async function saveInner(name: string) {

    const id = ulid();
    const now = new Date().toISOString();
    const contacts = allContacts(form, card);

    const record: LocalSupplier = {
      id,
      exhibitionId: exhibition.id,
      code: supplierCode(form.companyName || name),
      companyName: name,
      companyNameLocal: form.companyNameLocal || undefined,
      contactPerson: form.person || undefined,
      contactPersonLocal: form.personLocal || undefined,
      position: form.position || undefined,
      positionLocal: form.positionLocal || undefined,
      city: form.city || undefined,
      cityLocal: form.cityLocal || undefined,
      country: form.country || undefined,
      address: form.address || undefined,
      addressLocal: form.addressLocal || undefined,
      booth: form.booth || undefined,
      hall: form.hall || undefined,
      floor: form.floor || undefined,
      office: form.office || undefined,
      // Сайт читался и показывался, но в запись не попадал: у сорока одного
      // стенда заказчика он пуст, хотя на визитках был.
      website: form.website || undefined,
      // «Wholesaler of Ruby, Sapphire» с визитки — для геммолога важнее адреса.
      notes: card.notes || undefined,
      contacts,
      seq: 0,
      createdAt: now,
    };

    await db.suppliers.put(record);
    await enqueue('supplier.upsert', record, ulid());

    // Оригинал визитки сохраняем всегда: распознанное можно будет сверить.
    if (shot) {
      await db.media.put({
        id: ulid(), subjectId: id, role: 'card', blob: shot.full,
        mime: shot.full.type, bytes: shot.full.size, uploaded: false, createdAt: now,
      });
    }

    void syncNow();
    onDone(id);
  }

  // ── камера ────────────────────────────────────────────────────────────────
  if (phase === 'camera') {
    return (
      <div className="cam">
        <div className="lens">
          <video ref={videoRef} playsInline muted autoPlay />
          <div className="lenstop">
            <span className="glass" style={{ flex: 1 }}>Визитка целиком в кадр</span>
            <button className="glass round" onClick={onCancel} aria-label="Закрыть">✕</button>
          </div>
          <div className="guides"><i /><i /><i /><i /></div>
          {error && (
            <div className="readout">
              <div className="panel">{error}</div>
            </div>
          )}
        </div>
        <div className="deck">
          <button className="tool" onClick={() => setPhase('manual')} aria-label="Ввести вручную">⌨</button>
          <button className="trigger" onClick={() => void shoot()} disabled={Boolean(error)} aria-label="Снять визитку">
            <span className="pad" />
          </button>
          <span style={{ width: 50 }} />
        </div>
        <div className="status">Снимите — данные заполнятся сами</div>
      </div>
    );
  }

  // ── распознавание ─────────────────────────────────────────────────────────
  if (phase === 'reading') {
    return (
      <div className="app">
        <div className="bar"><h1 className="t">Читаю визитку</h1>
          <p className="meta">Несколько секунд. Китайский, тайский и иврит тоже.</p>
        </div>
        <div className="scroll">
          {shotUrl && <div className="plate"><img src={shotUrl} alt="визитка" /></div>}
          <div style={{ marginTop: 16, display: 'grid', gap: 10 }}>
            <div className="skeleton" style={{ height: 46 }} />
            <div className="skeleton" style={{ height: 46, width: '72%' }} />
            <div className="skeleton" style={{ height: 46, width: '84%' }} />
          </div>
        </div>
      </div>
    );
  }

  // ── проверка и правка ─────────────────────────────────────────────────────
  const doubtful = new Set(card.uncertain ?? []);
  const manualOnly = phase === 'manual';

  // Единственное, без чего поставщика не завести. Держим причину рядом с
  // кнопкой: серая кнопка без объяснения — это тупик, из которого человек
  // на выставке выходит только звонком.
  const needName = !form.companyName.trim() && !form.companyNameLocal.trim();

  /*
   * Названия на визитке может не быть вовсе — только имена людей, телефоны
   * и почта. Такие карточки в этом деле обычны, и человек у стенда упирался
   * в «не хватает названия компании», не понимая, что вписывать.
   *
   * Торговое имя на карточке всё-таки есть — в почте и в соцсетях. Предлагаем
   * его одним нажатием, но не подставляем молча: догадка должна остаться
   * видимой, её всегда можно поправить.
   */
  // Что уйдёт в карточку помимо трёх видимых полей.
  const shown = new Set([form.phone, form.email, form.wechat]
    .map((v) => v.trim()).filter(Boolean));
  const extras = allContacts(form, card).filter((c) => !shown.has(c.value));

  const person = (form.person || form.personLocal || '').trim();

  /*
   * Здание, заведённое по ориентиру, с первой же визитки может узнать своё
   * настоящее название и адрес: на гонконгских карточках они напечатаны
   * почти всегда. Предлагаем — не записываем молча: место общее для всех
   * камней, и подставить туда не то здание хуже, чем спросить.
   */
  const cardBuilding = pick(card.building);
  const cardAddress = pick(card.address);
  const placeGap = building && !placeSaved
    ? {
        name: !exhibition.name && cardBuilding ? cardBuilding : '',
        address: !exhibition.address && cardAddress ? cardAddress : '',
      }
    : { name: '', address: '' };
  const placeOffer = [placeGap.name, placeGap.address].filter(Boolean).join(', ');

  async function savePlace() {
    const ex: LocalExhibition = {
      ...exhibition,
      name: exhibition.name || placeGap.name,
      address: exhibition.address || placeGap.address || undefined,
    };
    await db.exhibitions.put(ex);
    // Код места не трогаем: по нему уже пронумерованы камни.
    await enqueue('exhibition.upsert', ex, ulid());
    setPlaceSaved(true);
    onPlaceUpdated?.(ex);
    void syncNow();
  }
  const suggestions = needName
    ? [
        guessCompanyName({
          emails: [form.email, ...(card.emails ?? [])].filter(Boolean),
          websites: [form.website, ...(card.websites ?? [])].filter(Boolean),
          handles: [form.wechat, card.line ?? '', card.telegram ?? ''].filter(Boolean),
        }),
        // Второй вариант — по человеку с карточки: у маленьких торговцев
        // стенд так и зовут, по имени хозяина.
        person,
      ].filter((v, i, all) => v && all.indexOf(v) === i)
    : [];

  // Знакомая визитка. Ищем по тому, что уже вписано в форму: человек мог
  // поправить название, и искать надо по исправленному.
  const twin = twinRejected ? null : findTwin(
    {
      companyName: form.companyName,
      companyNameLocal: form.companyNameLocal,
      contacts: [form.phone, form.wechat, form.email].filter(Boolean),
    },
    known,
    exhibition.id,
  );
  const toName = () => {
    setNudged(true);
    nameRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    nameRef.current?.focus({ preventScroll: true });
  };

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onCancel}>‹ {placeTitle(exhibition) || exhibition.code}</button>
        <h1 className="t">{manualOnly ? 'Новый поставщик' : 'Проверьте, что прочиталось'}</h1>
        <p className="meta">
          {manualOnly
            ? 'Достаточно названия компании.'
            : 'Исправьте, если где-то ошибка. Фото визитки сохранится в любом случае.'}
        </p>
      </div>

      <div className="scroll">
        {error && <div className="memo" style={{ marginTop: 0 }}>{error}</div>}

        {/*
          * Второй подход к тому же поставщику — обычное дело: принесли ещё
          * камни, сменился стенд, дали другую визитку. Раньше приложение
          * молча заводило двойника, камни разъезжались по двум карточкам,
          * а номера у них совпадали и запись могла не сохраниться вовсе.
          */}
        {placeOffer && (
          <div className="memo" style={{ marginTop: 0 }}>
            <b>На визитке есть, где вы.</b>
            <br />Записать в здание «{placeTitle(exhibition)}»: {placeOffer}?
            <div className="states" style={{ marginTop: 10 }}>
              <button className="state-btn" aria-pressed onClick={() => void savePlace()}>Да, записать</button>
              <button className="state-btn" onClick={() => setPlaceSaved(true)}>Нет</button>
            </div>
          </div>
        )}

        {twin && (
          <div className="memo" style={{ marginTop: 0, borderColor: 'var(--line-strong)' }}>
            <b>Такой стенд уже есть.</b>
            <br />{twin.supplier.companyName}
            {whereIs(twin.supplier) ? ` · ${whereIs(twin.supplier)}` : ''} — {twin.reason}.
            <br />Добавить визитку и контакты к нему? Камни продолжат нумероваться подряд.
            <div className="states" style={{ marginTop: 12 }}>
              <button className="state-btn" aria-pressed disabled={merging}
                      onClick={() => void addToExisting(twin)}>
                {merging ? 'Добавляю…' : 'Да, это он'}
              </button>
              <button className="state-btn" disabled={merging}
                      onClick={() => setTwinRejected(true)}>
                Нет, другой стенд
              </button>
            </div>
          </div>
        )}
        {shotUrl && !manualOnly && (
          <div className="plate" style={{ marginBottom: 14 }}><img src={shotUrl} alt="визитка" /></div>
        )}

        <label className={`field${needName && nudged ? ' need' : ''}`}>
          <span>
            Компания{needName ? ' · нужно' : doubtful.has('companyName') ? ' · проверьте' : ''}
          </span>
          <input ref={nameRef} value={form.companyName} onChange={set('companyName')}
                 placeholder="Liming Gems Co., Ltd" />
        </label>

        {(form.companyNameLocal || !manualOnly) && (
          <label className="field">
            <span>Как напечатано на визитке</span>
            <input value={form.companyNameLocal} onChange={set('companyNameLocal')} placeholder="广州黎明宝石有限公司" />
            <span className="hint">Оригинал храним отдельно — его показывают поставщику.</span>
          </label>
        )}

        <div className="pair">
          <label className="field">
            <span>Кто</span>
            <input value={form.person} onChange={set('person')} placeholder="Li Wei" />
          </label>
          <label className="field">
            <span>Оригинал</span>
            <input value={form.personLocal} onChange={set('personLocal')} placeholder="李伟" />
          </label>
        </div>

        <div className="pair">
          <label className="field"><span>{building ? 'Этаж' : 'Стенд'}</span>
            <input value={building ? form.floor : form.booth} onChange={set(building ? 'floor' : 'booth')}
                   placeholder={building ? '12' : '3B-14'} /></label>
          <label className="field"><span>{building ? 'Офис' : 'Зал'}</span>
            <input value={building ? form.office : form.hall} onChange={set(building ? 'office' : 'hall')}
                   placeholder={building ? '1205' : '3'} /></label>
        </div>

        <label className="field">
          <span>Телефон{doubtful.has('phones') ? ' · проверьте' : ''}</span>
          <input value={form.phone} onChange={set('phone')} inputMode="tel" />
        </label>
        <label className="field"><span>WeChat</span>
          <input value={form.wechat} onChange={set('wechat')} autoCapitalize="off" /></label>
        <label className="field"><span>Email</span>
          <input value={form.email} onChange={set('email')} inputMode="email" autoCapitalize="off" /></label>

        <label className="field"><span>Сайт</span>
          <input value={form.website} onChange={set('website')} inputMode="url" autoCapitalize="off" /></label>

        {/* Остальное, что прочиталось: второй телефон напарника, вторая почта,
            мессенджеры. Полей под них нет, но и молчать о них нельзя —
            сохраняются они всё равно. */}
        {extras.length > 0 && (
          <p className="meta" style={{ marginTop: -4, marginBottom: 14 }}>
            С визитки сохранится ещё: {extras.map((c) => c.value).join(', ')}.
          </p>
        )}
        {card.notes && (
          <p className="meta" style={{ marginTop: -4, marginBottom: 14, whiteSpace: 'pre-line' }}>
            В заметку о стенде: {card.notes}
          </p>
        )}

        <div className="pair">
          <label className="field"><span>Город</span>
            <input value={form.city} onChange={set('city')} placeholder="Guangzhou" /></label>
          <label className="field"><span>Страна</span>
            <input value={form.country} onChange={set('country')} placeholder="China" /></label>
        </div>
      </div>

      <div className="dock">
        {needName && (
          suggestions.length ? (
            <div className="why">
              <b>На визитке нет отдельной строки с названием.</b>
              <span>Назвать стенд так? Нажмите — впишется, потом можно поправить.</span>
              <div className="states" style={{ marginTop: 10 }}>
                {suggestions.map((v) => (
                  <button key={v} className="state-btn"
                          onClick={() => { setForm({ ...form, companyName: v }); setNudged(false); }}>
                    {v}
                  </button>
                ))}
                <button className="state-btn" onClick={toName}>Вписать своё</button>
              </div>
            </div>
          ) : (
            <button type="button" className="why" onClick={toName}>
              <b>Не хватает названия компании.</b>
              <span>Оно в начале формы — нажмите, чтобы вписать.</span>
            </button>
          )
        )}
        <button className="btn btn-secondary" onClick={onCancel}>Отмена</button>
        <button className="btn btn-primary"
                onClick={() => (needName ? toName() : void save())}>
          Сохранить и снимать
        </button>
      </div>
    </div>
  );
}
